import { ColDef, GetContextMenuItemsParams, MenuItemDef } from "ag-grid-community"
import { AgGridReact } from "ag-grid-react"
import { useCallback, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import {
  CellStyleProtocol,
  columnsDefCellStyleMapping,
} from "../../PortfolioManagement/components/agGridMapping"
import { useAgGrid } from "../../PortfolioManagement/components/hooks/useAgGrid"
import { DataframeData } from "../../PortfolioManagement/types"
import { useDatavizColumns } from "../hooks/useDatavizColumns"
import {
  OutputTransformation,
  POP_TYPES_CONVERTIBLE_TO_INDEX,
  POP_TYPES_CONVERTIBLE_TO_YOY,
} from "../types/dashboard"
import { DashboardItemForm, DashboardUpdateCreateForm } from "../types/dashboardForm"
import RemoveButtonRenderer from "./RemoveButtonRenderer"

interface DatavizTableProps {
  apiData: DataframeData
}

/**
 * Check if a PoP type string contains any of the convertible types
 */
const isPopTypeConvertible = (
  popDataType: string | null | undefined,
  convertibleTypes: string[],
): boolean => {
  if (!popDataType) return false
  return convertibleTypes.some(type => popDataType.includes(type))
}

/**
 * Check if data is a PoP type (period-over-period)
 */
const isPopData = (popDataType: string | null | undefined): boolean => {
  return isPopTypeConvertible(popDataType, POP_TYPES_CONVERTIBLE_TO_INDEX)
}

const DatavizTable = ({ apiData }: DatavizTableProps) => {
  const { setValue, getValues } = useFormContext<DashboardUpdateCreateForm>()

  const { gridRef, gridOptions } = useAgGrid({
    columns: apiData.columns,
    initialScrollPosition: apiData.initialScrollPosition,
    masks: apiData.masks,
  })

  const handleRemoveRow = useCallback(
    (data: any) => {
      const items = getValues("items") as DashboardItemForm[]
      const queryKey = (data as any)?.query_key
      const nextItems = items.filter(item => {
        if (data.query_key.startsWith("dataset:")) {
          return item.dataset_id !== parseInt(queryKey.split(":")[1])
        } else if (data.query_key.startsWith("funda:")) {
          // Split the query key into values ex funda:1|GROUP;PROFIT|S14|Q|A
          const query_key_values = queryKey.split(":")[1].split("|")
          const [companyIdStr, metric, source, periodicity] = query_key_values
          const companyId = parseInt(companyIdStr, 10)
          // Keep all items except the exact funda match
          return !(
            item.company_id === companyId &&
            item.fundamentals_metric === metric &&
            item.fundamentals_source === source &&
            item.fundamentals_periodicity === periodicity
          )
        }
        return true
      })
      setValue("items", nextItems, { shouldDirty: true, shouldValidate: true, shouldTouch: true })
    },
    [getValues, setValue],
  )

  /**
   * Handle transformation change from context menu
   */
  const handleTransformationChange = useCallback(
    (idx: number, transformation: OutputTransformation) => {
      const items = getValues("items") as DashboardItemForm[]
      const updatedItems = items.map((item, index) =>
        index === idx ? { ...item, output_transformation: transformation } : item,
      )
      setValue("items", updatedItems, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      })
    },
    [getValues, setValue],
  )

  /**
   * Build context menu for right-click - context-aware based on data type
   */
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
      const idx = params.node?.data?._idx as number | undefined
      const currentTransformation = params.node?.data?.transformation as
        | OutputTransformation
        | undefined
      const popDataType = params.node?.data?.pop_data_type as string | undefined

      if (idx === undefined) return []

      // Build transformation submenu based on data type
      const transformationSubMenu: MenuItemDef[] = [
        {
          name: "None",
          icon:
            !currentTransformation || currentTransformation === OutputTransformation.NONE
              ? "✓"
              : "",
          action: () => handleTransformationChange(idx, OutputTransformation.NONE),
        },
      ]

      // Show "Convert to Index" only for PoP data that can be indexed
      if (isPopTypeConvertible(popDataType, POP_TYPES_CONVERTIBLE_TO_INDEX)) {
        transformationSubMenu.push({
          name: "Convert to Index",
          icon: currentTransformation === OutputTransformation.INDEX ? "✓" : "",
          action: () => handleTransformationChange(idx, OutputTransformation.INDEX),
        })
      }

      // Show YoY only if:
      // - Data is not PoP (level data), OR
      // - Data is PoP that can be converted to YoY (MoM%, QoQ%, HoH%)
      if (
        !isPopData(popDataType) ||
        isPopTypeConvertible(popDataType, POP_TYPES_CONVERTIBLE_TO_YOY)
      ) {
        transformationSubMenu.push({
          name: "Year-over-Year (YoY)",
          icon: currentTransformation === OutputTransformation.YOY ? "✓" : "",
          action: () => handleTransformationChange(idx, OutputTransformation.YOY),
        })
      }

      // Show Sequential only for non-PoP data (level data)
      if (!isPopData(popDataType)) {
        transformationSubMenu.push({
          name: "Sequential (Seq)",
          icon: currentTransformation === OutputTransformation.SEQ ? "✓" : "",
          action: () => handleTransformationChange(idx, OutputTransformation.SEQ),
        })
      }

      return [
        {
          name: "Transformation",
          subMenu: transformationSubMenu,
        },
        "separator",
        {
          name: "Remove",
          action: () => handleRemoveRow(params.node?.data),
        },
      ]
    },
    [handleTransformationChange, handleRemoveRow],
  )

  /**
   * Add a remove column to the table
   */
  const columnDefs = useMemo(() => {
    const removeColumn: ColDef = {
      field: "remove",
      headerName: "",
      width: 38,
      cellRenderer: RemoveButtonRenderer,
      cellRendererParams: {
        onRemove: handleRemoveRow,
      },
      suppressMenu: true,
      sortable: false,
      filter: false,
      pinned: "left",
      cellStyle: columnsDefCellStyleMapping[CellStyleProtocol.WHITE],
    }

    const baseColumns = gridOptions.columnDefs || []

    return [removeColumn, ...baseColumns]
  }, [gridOptions.columnDefs, handleRemoveRow])

  const columnDefsWithWidth = useDatavizColumns(columnDefs)

  return (
    <AgGridReact
      ref={gridRef}
      rowData={apiData.rows}
      defaultColDef={{
        ...gridOptions.defaultColDef,
        tooltipValueGetter: (params: any) => {
          // Default tooltip - will be overridden by column-specific tooltips
          return undefined
        },
      }}
      columnDefs={columnDefsWithWidth}
      getRowStyle={gridOptions.getRowStyle}
      onRowDataUpdated={gridOptions.onRowDataUpdated}
      onModelUpdated={gridOptions.onModelUpdated}
      onGridReady={gridOptions.onGridReady}
      onFirstDataRendered={gridOptions.onFirstDataRendered}
      rowHeight={25}
      headerHeight={30}
      getContextMenuItems={getContextMenuItems}
      tooltipShowDelay={0}
      tooltipHideDelay={1000000}
    />
  )
}

export default DatavizTable
